import React, {useState, useEffect} from "react";
import BackButton from "components/Button/BackButton";
import SaveButton from "components/Button/SaveButton";
import useFetch from "utils/hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { FormTypeAdd, FormTypeEdit, ModalTypeConfirmation, ModalTypeFail, ModalTypeLoading, ModalTypeSuccess, Undefined } from "utils/Constant";
import { ApiGetProductById } from "utils/constants/api";
import { CheckResponse, money, removeNonNumeric } from "utils/helpers";
import { useModal } from "context/ModalContext";
import Select from 'react-select'
import ImageIcon from "assets/icon/ImageIcon";
import { FormCard, MainBodyCard, PagesFormTitle } from "components/Cards";
import { useCallApi } from "api/apiHooks";
import { SystemService } from "services/SystemService/SystemService";
import { BrandService } from "services/BrandService/BrandService";
import { CategoryService } from "services/CategoryService/CategoryService";
import { ProductService } from "services/ProductService/ProductService";

export default function ProductForm(){
    let navigate = useNavigate()

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchSystem } = SystemService()
    const { fetchBrandList } = BrandService()
    const { fetchCategoryList } = CategoryService()
    const { fetchCreateProduct, fetchUpdateProduct, fetchProductById } = ProductService()

    const { fetch: apiGetProductById } = useFetch(ApiGetProductById)
    
    let queryString = new URLSearchParams(window.location.search)
    let formType = queryString.get("formType")
    let productId = queryString.get("productId")

    const [brandOptions, setBrandOptions] = useState([])
    const [productCode, setProductCode] = useState('')
    const [brand, setBrand] = useState('')
    const [categoryOptions, setCategoryOptions] = useState([])
    const [category, setCategory] = useState([])
    const [productName, setProductName] = useState('')
    const [description, setDescription] = useState('-')
    const [price, setPrice] = useState('')
    const [priceAfterDiscount, setPriceAfterDiscount] = useState('')
    const [discount, setDiscount] = useState('0')
    const [status, setStatus] = useState('')
    const [createBy, setCreateBy] = useState('')
    const [createDate, setCreateDate] = useState('')
    const [updateBy, setUpdateBy] = useState('')
    const [updateDate, setUpdateDate] = useState('')
    const [productPhotoFile, setProductPhotoFile] = useState('')
    const [productPhotoFileUrl, setProductPhotoFileUrl] = useState('')
    const [tax, setTax] = useState('')

    // Event
    const handleProductPhotoChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // ✅ Validate file size (1MB = 1024 * 1024)
        const maxSize = 1024 * 1024; // 1MB

        if (file.size > maxSize) {
            showFail('Image size must be below 1MB!')
            return
        }

        // (optional) ✅ Validate image type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

        if (!allowedTypes.includes(file.type)) {
            showFail('Only JPG, JPEG, PNG allowed!')
            return
        }

        // ✅ If valid
        setProductPhotoFile(file)
        setProductPhotoFileUrl(URL.createObjectURL(file))
    }
    
    // Button Event
    const saveBtnClick = async () => {
        if(!validation()) return 

        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            if (formType === FormTypeAdd) {
                const res = await fetchCreateProduct(
                    brand.value, 
                    productCode,
                    category.value, 
                    productName, 
                    description,
                    price.replaceAll(',', ''), 
                    priceAfterDiscount, 
                    discount, 
                    productPhotoFile
                )
                if (!res) return            
            } else {
                const res = await fetchUpdateProduct(
                    productId,
                    productCode,
                    brand.value,
                    category.value, 
                    productName, 
                    description,
                    price.replaceAll(',', ''), 
                    priceAfterDiscount, 
                    discount, 
                    productPhotoFile
                )
                if (!res) return
            }
        }
        finally {
            hideLoading()
        }

        showSuccess({
            onSuccess: () => {
                navigate('/product')
            }
        })
    }
    const backBtnClick = () => {
        navigate(-1)
    }

    // Function
    const validation = () => {
        if (productCode === null){
            showFail('Product Code is required!')
            return false 
        } else if (brand === null){
            showFail('Brand is required!')
            return false
        } else if (typeof(brand) === Undefined) {
            showFail('Brand is required!')
            return false
        } else if (brand.value === '') {
            showFail('Brand is required!')
            return false
        }
        if (category === null){
            showFail('Category is required!')
            return false
        } else if (typeof(category) === Undefined) {
            showFail('Category is required!')
            return false
        } else if (category.value === '') {
            showFail('Category is required!')
            return false
        }
        if (productName === '') {
            showFail('Product Name is required!')
            return false
        }
        if (price === '') {
            showFail('Price is required!')
            return false
        }
        if (discount === '') {
            showFail('Discount is required!')
            return false
        } else {
            if (parseInt(discount) > 100){
                showFail('Discount cannot exceeded 100!')
                return false
            }
        }

        return true
    }
    
    useEffect(() => {
        if (price !== ''){
            let disc = discount === '' ? 0 : parseFloat(discount)
            let finalPrice = (parseFloat(price.replaceAll(',', '')) * (100 - disc) / 100) / ((100-parseInt(tax))/100)

            setPriceAfterDiscount(Math.ceil(finalPrice / 1000) * 1000)
        }
    }, [price, discount, tax])
    
    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                let res = await fetchSystem('tax')
                if (!res) return

                setTax(res.data.systemValue)

                res = await fetchBrandList('', '1', 1000, 0)
                if (!res) return

                let data = res?.data?.listData
                let arr = [{ label: 'Select Brand', value: '' }]

                data.map((data) => arr.push({ label: data.brandName, value: String(data.brandId)}))

                setBrandOptions(arr)

                res = await fetchCategoryList('', '1', 1000, 0)
                if (!res) return

                data = res?.data?.listData
                arr = [{ label: 'Select Category', value: '' }]

                data.map((data) => arr.push({ label: data.categoryName, value: String(data.categoryId)}))

                setCategoryOptions(arr)

                if (formType === FormTypeEdit) {
                    const res = await fetchProductById(productId)
                    if (!res) return
                    
                    let data = res.data

                    setBrand({ label: data.brandName, value: data.brandId })
                    setCategory(arr.find((x) => x.value === String(data.categoryId)))
                    setProductCode(data.productCode)
                    setProductName(data.productName)
                    setDescription(data.description === '' ? '-' : data.description)
                    setPrice(money(data.price))
                    setDiscount(data.discount)
                    setStatus(data.status)
                    setCreateBy(data.createBy)
                    setCreateDate(data.createDate)
                    setUpdateBy(data.updateBy)
                    setUpdateDate(data.updateDate)
                    setProductPhotoFileUrl(data.productPictureUrl)
                    setPriceAfterDiscount(data.nettPrice)
                }
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    }, [])

    return(
        <MainBodyCard>
            <div>
                <PagesFormTitle 
                    title={'Product Detail'}
                    formType={formType}
                    status={status}
                    createDate={createDate}
                    updateDate={updateDate}
                    updateBy={updateBy}
                />

                <div className="mt-5 flex flex-col gap-3 ">
                    {/* Product Photo */}
                    <div className="flex flex-col sm:flex-row sm:items-center">
                        <div className="w-48">
                            <span>Product Photo</span>
                        </div>
                        <div className="px-4 hidden sm:block">
                            <span>:</span>
                        </div>
                        <div className="w-full sm:w-72">
                            <img
                                className="w-auto h-[200px] object-cover mx-auto border rounded-md"
                                src={productPhotoFileUrl}
                                alt="ID"
                            />
                            <input className="hidden" id="productPhoto" type="file" accept=".jpg, .png, .jpeg, .gif|image/*" onChange={handleProductPhotoChange} />
                            <button onClick={() => document.getElementById('productPhoto').click()} 
                                className="mt-2 px-3 py-2 bg-app-matte-black text-white rounded-md text-center cursor-pointer w-full flex gap-2 justify-center"
                            >
                                <ImageIcon />
                                Add Image
                            </button>
                        </div>
                    </div>

                    {/* Product Code */}
                    <FormCard title='Product Code'>
                        <input className="input" value={productCode} onChange={(e) => { setProductCode(e.target.value) }} maxLength={5} />
                    </FormCard>

                    {/* Brand */}
                    <FormCard title='Brand'>
                        <Select options={brandOptions} value={brand} onChange={(e) => { setBrand(e) }} placeholder='Select Brand' />
                    </FormCard>

                    {/* Product Name */}
                    <FormCard title='Product Name'>
                        <input className="input" value={productName} onChange={(e) => { setProductName(e.target.value) }} maxLength={40} />
                    </FormCard>
                    
                    {/* Description */}
                    <FormCard title='Description'>
                        <input className="input" value={description} onChange={(e) => { setDescription(e.target.value) }} maxLength={40} />
                    </FormCard>

                    {/* Category */}
                    <FormCard title='Category'>
                        <Select options={categoryOptions} value={category} onChange={(e) => { setCategory(e) }} placeholder='Select Category' />
                    </FormCard>

                    {/* Price */}
                    <FormCard title='Selling Price'>
                        <input className="input" value={price} onChange={(e) => { setPrice(money(e.target.value)) }} maxLength={40} />
                    </FormCard>

                    {/* Discount */}
                    <FormCard title='Discount'>
                        <div className="flex items-center">
                            <input className="border rounded-lg py-2 px-3 w-16" value={discount} onChange={(e) => { setDiscount(removeNonNumeric(e.target.value)) }} maxLength={3} />
                            <span className="ml-2">%</span>
                        </div>
                    </FormCard>

                    {/* Tax */}
                    <FormCard title='Tax'>
                        <span>{tax} %</span>
                    </FormCard>

                    {/* Final Price */}
                    <FormCard title='Final Price'>
                        <span>{money(priceAfterDiscount)}</span>
                    </FormCard>
                </div>
            </div>            

            {/* Button */}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
                <SaveButton onClick={saveBtnClick} visible={true} />
                <BackButton onClick={backBtnClick} />
            </div>
        </MainBodyCard>        
    )
}
